import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Form } from 'react-bootstrap'
import {
  getFeaturedConfig,
  updateFeaturedConfig,
  getHotelsListForFeatured,
  getToursListForFeatured,
  FeaturedConfig,
  HotelListItem,
  TourListItem,
} from '../api/featuredConfigApi'

const FeaturedConfigTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<FeaturedConfig | null>(null)
  const [hotels, setHotels] = useState<HotelListItem[]>([])
  const [tours, setTours] = useState<TourListItem[]>([])
  const [selectedHotelIds, setSelectedHotelIds] = useState<string[]>([])
  const [selectedTourIds, setSelectedTourIds] = useState<string[]>([])
  const [hotelSearch, setHotelSearch] = useState('')
  const [tourSearch, setTourSearch] = useState('')

  const loadData = async () => {
    try {
      setLoading(true)
      const [configRes, hotelsList, toursList] = await Promise.all([
        getFeaturedConfig(),
        getHotelsListForFeatured(),
        getToursListForFeatured(),
      ])
      const cfg = configRes?.data ?? configRes
      setConfig(cfg || null)
      setHotels(hotelsList || [])
      setTours(toursList || [])
      setSelectedHotelIds(cfg?.featuredHotelIds ?? [])
      setSelectedTourIds(cfg?.featuredTourIds ?? [])
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.ERROR_LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const toggleHotel = (id: string) => {
    setSelectedHotelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleTour = (id: string) => {
    setSelectedTourIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await updateFeaturedConfig({
        featuredHotelIds: selectedHotelIds,
        featuredTourIds: selectedTourIds,
      })
      toast.success(intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.SUCCESS_SAVE' }))
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.ERROR_SAVE' }))
    } finally {
      setSaving(false)
    }
  }

  const hotelName = (h: HotelListItem) =>
    h.translations?.[0]?.name ?? (h as any).name ?? h._id
  const tourTitle = (t: TourListItem) =>
    t.translations?.[0]?.title ?? (t as any).title ?? t._id

  const filteredHotels = hotels.filter((h) =>
    hotelName(h).toLowerCase().includes(hotelSearch.trim().toLowerCase()),
  )
  const filteredTours = tours.filter((t) =>
    tourTitle(t).toLowerCase().includes(tourSearch.trim().toLowerCase()),
  )

  if (loading) {
    return (
      <div className="text-muted">
        {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}
      </div>
    )
  }

  return (
    <div>
      <p className="text-muted mb-4">
        {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.DESCRIPTION' })}
      </p>

      <div className="row">
        <div className="col-md-6">
          <h5 className="mb-3">
            {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.HOTELS' })}
          </h5>
          <input
            type="search"
            className="form-control form-control-solid mb-3"
            placeholder={intl.formatMessage({ id: 'COMMON.SEARCH', defaultMessage: 'Search' })}
            value={hotelSearch}
            onChange={(e) => setHotelSearch(e.target.value)}
          />
          <div className="border rounded p-3 mb-4" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {filteredHotels.length === 0 ? (
              <span className="text-muted">
                {intl.formatMessage({ id: 'COMMON.EMPTY_DATA' })}
              </span>
            ) : (
              filteredHotels.map((h) => (
                <Form.Check
                  key={h._id}
                  type="checkbox"
                  id={`hotel-${h._id}`}
                  label={hotelName(h)}
                  checked={selectedHotelIds.includes(h._id)}
                  onChange={() => toggleHotel(h._id)}
                />
              ))
            )}
          </div>
        </div>
        <div className="col-md-6">
          <h5 className="mb-3">
            {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.TOURS' })}
          </h5>
          <input
            type="search"
            className="form-control form-control-solid mb-3"
            placeholder={intl.formatMessage({ id: 'COMMON.SEARCH', defaultMessage: 'Search' })}
            value={tourSearch}
            onChange={(e) => setTourSearch(e.target.value)}
          />
          <div className="border rounded p-3 mb-4" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {filteredTours.length === 0 ? (
              <span className="text-muted">
                {intl.formatMessage({ id: 'COMMON.EMPTY_DATA' })}
              </span>
            ) : (
              filteredTours.map((t) => (
                <Form.Check
                  key={t._id}
                  type="checkbox"
                  id={`tour-${t._id}`}
                  label={tourTitle(t)}
                  checked={selectedTourIds.includes(t._id)}
                  onChange={() => toggleTour(t._id)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.SAVING' })
            : intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.FEATURED.SAVE' })}
        </button>
      </div>
    </div>
  )
}

export default FeaturedConfigTab
